// "Pause all watching" — the web half of the seasonal lay-up switch (ADR 0174 /
// core ADR-111).
//
// THE BOAT OWNS IT, AND THE BOAT ENFORCES IT. Every surface here relays the
// instruction down and then waits for the boat's own echo to come back through
// the mirror; nothing is applied optimistically. That matters more here than
// anywhere else in the app: the banner this module writes says NOTHING IS BEING
// WATCHED, and an owner who reads that goes away and stops expecting mail. If
// the banner could appear on a click rather than on the boat's confirmation, the
// one time it lied would be the time the command never landed — a boat sounding
// alarms into an empty shed while its owner believes it is asleep, or worse, the
// mirror image: an owner told the boat is paused, no notifications expected, and
// a bilge alarm firing to nobody.
//
// So the states are the honest ones — `sending` while the relay is in flight,
// `sent` while the boat has the command but the mirror has not moved, `offline`
// when it could not be delivered at all — and the banner itself is driven by
// `alarmsPausedAt` from the state read, which only the boat's up-sync writes.
//
// Pure module: no React, no DOM. The components render its answers, and the unit
// tests run without a browser.

import { api } from '../api';
import { formatRelative } from './deviceSeen';
import { isBoatOfflineRelayError } from './revealStaging';

// ---------------------------------------------------------------------------
// The words.
// ---------------------------------------------------------------------------

/** The banner's headline while the boat is paused. Deliberately blunt. */
export const ALARMS_PAUSED_HEADLINE = 'Alarms paused — nothing is being watched.';

/** The resume affordance. One tap, no confirmation: resuming is never the risk. */
export const RESUME_LABEL = 'Resume watching';

/**
 * The active band's empty line while the boat is paused.
 *
 * "All quiet — nothing needs your attention" is a claim about the WORLD, and it
 * is only earned by watching. On a paused boat it is the same sentence a
 * perfectly healthy boat shows, printed in green, directly under a banner saying
 * nothing is being watched — which is precisely the reassurance-without-evidence
 * this redesign exists to remove.
 */
export const ALARMS_PAUSED_BAND_EMPTY =
  'Nothing is being watched, so nothing here can need attention.';

/** The quiet affordance's question, shown while the boat IS watching. */
export const PAUSE_AFFORDANCE_QUESTION = 'Pausing for the season?';

/** The quiet affordance's button. It opens the confirm; it pauses nothing. */
export const PAUSE_LABEL = 'Pause all watching';

/**
 * The confirm's committing action — the tap that actually relays. Worded
 * differently from the affordance above so the two are never confused at a
 * glance, and identically to the boat's own settings sheet, because an owner who
 * has done this at the helm should recognise it from the sofa.
 */
export const PAUSE_CONFIRM_COMMIT = 'Yes, pause everything';

/** The confirm's heading — a question, because it is asking. */
export const PAUSE_CONFIRM_TITLE = 'Pause all alarm watching?';

/**
 * What pausing actually does, in the order an owner needs it: what stops, what
 * happens to an alarm that is sounding right now, and what comes back.
 *
 * The emergency class is NAMED. "No alarms" is technically complete and reads,
 * to someone thinking about a low battery in a shed, as though the life-safety
 * ones are obviously excepted. They are not — the pause is per-BOAT and holds
 * every rule aboard, including smoke/CO and high water — and an owner who
 * assumed otherwise has been told something false by an omission.
 */
export const PAUSE_CONFIRM_SCOPE =
  'Nothing on this boat will be watched until you resume — no alarms, and no notifications on or off the boat, including smoke/CO, high water, and every emergency alarm — nothing is exempt.';

/**
 * The promise that makes this safe to tap, and the whole reason the feature is a
 * gate rather than switching twenty alarms off by hand: nothing is edited, so
 * nothing has to be remembered and restored.
 */
export const PAUSE_CONFIRM_RESTORE =
  'Your alarms keep their settings — resuming brings back exactly what is being watched now.';

/**
 * The in-flight guarantee, in the same words the boat's own settings sheet uses.
 * It is a statement about the machinery, not a hedge: the evaluator refuses a
 * disable against an alarm that is currently Active and holds it until the alarm
 * resolves, so pausing cannot silence something that is sounding right now.
 */
export const PAUSE_CONFIRM_IN_FLIGHT = 'An alarm that is sounding right now finishes first.';

/**
 * The remedy for the case the sentence above quietly creates, and the reason it
 * is stated rather than left to be discovered.
 *
 * "Finishes first" assumes an alarm that CAN finish. The lay-up case routinely
 * produces one that cannot: instruments switched off at the panel leave a
 * data-unavailable alarm Active with nothing left to clear it, the disable-hold
 * never releases, and the buzzer sounds into a shed the owner has just driven
 * away from.
 *
 * The wording is what the machinery really does, traced end to end rather than
 * assumed. Acknowledging from here relays `alert_command {action:'ack'}` to the
 * boat, which dispatches ACK on its own evaluator: the annunciator stops
 * immediately (only `Active` sounds the buzzer — `Acknowledged` does not), but
 * `Acknowledged` is still an IN-FLIGHT state, so the held pause is NOT released
 * by the ack. It applies when the alarm actually clears. So the copy promises
 * silence, which is true and is what the owner needs, and does not promise the
 * pause takes hold, which would not be.
 */
export const PAUSE_CONFIRM_STUCK_REMEDY =
  'If one is stuck sounding — instruments switched off at the panel, say — acknowledge it here: that silences it, and the pause takes hold when it clears.';

/** Cancel, on the confirm. */
export const PAUSE_CONFIRM_CANCEL = 'Keep watching';

/**
 * Why the control is disabled while the boat is unreachable — VISIBLE text, not
 * a tooltip.
 *
 * The offline case is not an edge here, it is the NORM: a boat is laid up
 * precisely because it is in a shed with its batteries out, so the owner who
 * comes back in March to resume is the likeliest visitor to this banner and
 * meets a dead button. A `title` attribute answers that for a mouse and for
 * nobody else — most of these visits are from a phone, where there is no hover
 * and the reason simply does not exist.
 *
 * Two lines, because the two directions are not the same news. A paused boat
 * that is offline is still genuinely paused (the boat holds the pause itself,
 * with no cloud in the path), so nothing is wrong and nothing is at risk. A
 * WATCHING boat that is offline is not paused and will not become paused by
 * tapping.
 */
export const RESUME_OFFLINE_REASON =
  'Your boat is offline — it keeps the pause itself, so nothing is being watched either way. Resume from the boat, or from here once it is back online.';
export const PAUSE_OFFLINE_REASON =
  'Your boat is offline — your boat is what stops watching, so this cannot pause it from here. Pause from the boat, or from here once it is back online.';

/**
 * The count sentence, shown only when the active band actually has rows — the
 * number comes FROM that band, so the confirm can never claim an alarm the page
 * is not showing. `null` when nothing is sounding: an owner pausing a quiet boat
 * should not be handed a sentence about alarms to parse.
 *
 * `null` for `activeCount` means UNKNOWN, and unknown is not zero. The cloud
 * has mirrored no runtime for this boat — a boat it has never heard runtime
 * from, or a page that opened before the first mirror landed — so it cannot say
 * nothing is sounding, and saying nothing at all would let the confirm imply a
 * quiet boat it has no evidence for. The caveat is the honest middle.
 */
export function pauseConfirmActiveLine(activeCount: number | null): string | null {
  if (activeCount === null) {
    return 'Your boat has not told this page what it is watching yet, so it cannot say whether anything is sounding right now.';
  }
  if (!Number.isFinite(activeCount) || activeCount <= 0) return null;
  const n = Math.trunc(activeCount);
  return n === 1
    ? '1 alarm needs attention right now, so the pause takes hold once it clears.'
    : `${n} alarms need attention right now, so the pause takes hold once they clear.`;
}

/** How long a paused boat has been paused, in one phrase. */
export function pausedSinceLabel(pausedAt: string | null | undefined, now = Date.now()): string {
  const iso = typeof pausedAt === 'string' ? pausedAt : null;
  const ms = iso === null ? NaN : Date.parse(iso);
  // A pause with no readable moment is STILL a pause — the banner's headline is
  // the load-bearing half and must not disappear because a date did.
  if (iso === null || !Number.isFinite(ms)) return 'Paused.';
  // A lay-up is measured in months, so past `formatRelative`'s 30-day cliff the
  // date itself reads better than "153 days ago" — and it is the form an owner
  // can check against the day they left the boat. Inside 30 days the shared
  // wording is used verbatim, so "paused 3 hours ago" says the same thing here
  // as everywhere else in the app.
  if (now - ms < THIRTY_DAYS_MS) return `Paused ${formatRelative(iso, now).toLowerCase()}`;
  return `Paused on ${new Date(ms).toLocaleDateString()}`;
}

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60_000;

/** Is the boat paused, per the mirror? Absent/unparseable ⇒ watching. */
export function isAlarmsPaused(pausedAt: string | null | undefined): boolean {
  return typeof pausedAt === 'string' && Number.isFinite(Date.parse(pausedAt));
}

// ---------------------------------------------------------------------------
// The relay call, and the honest states around it.
// ---------------------------------------------------------------------------

/**
 * What the control is doing right now.
 *
 * `sent` is the interesting one: the command reached the boat (or is staged for
 * it), and the mirror has not caught up. It is NOT success-with-a-spinner — the
 * boat re-validates at its own trust boundary — so the copy says "Sent to your
 * boat…" and the banner does not change until the boat says it did.
 *
 * `sent` is an IN-FLIGHT REQUEST, owned by the page, and it is the page that
 * ends it: {@link settleAlarmsPause} on the boat's echo, {@link timeOutAlarmsPause}
 * when no echo arrives. It is deliberately NOT derived from "the mirror
 * disagrees with what I sent", which was the earlier shape and was wrong in a
 * way that misleads: an owner who resumes at the helm hours later flips the
 * mirror back, the derived mismatch returns, and a page nobody has touched
 * starts claiming a command is on its way to the boat.
 */
export type AlarmsPauseSaveState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'sending'; readonly paused: boolean }
  | { readonly kind: 'sent'; readonly paused: boolean }
  | { readonly kind: 'unconfirmed'; readonly paused: boolean }
  | { readonly kind: 'offline'; readonly paused: boolean }
  | { readonly kind: 'error' };

// The deadline this control gives up at lives in `./boatEcho` as
// `BOAT_ECHO_TIMEOUT_MS`, shared with the vessel-type control so the two never
// stop waiting at different moments. Imported directly by the component rather
// than re-exported from here — one constant, one import path.

/**
 * The boat's echo landed — end the in-flight request. Pure, so the page can run
 * it in an effect off the mirrored value and the tests can drive it without a
 * DOM.
 *
 * Returns the SAME object when nothing changes, so an effect calling
 * `setSave(settleAlarmsPause(save, paused))` on every render cannot loop.
 */
export function settleAlarmsPause(
  save: AlarmsPauseSaveState,
  mirroredPaused: boolean
): AlarmsPauseSaveState {
  if (save.kind !== 'sent' && save.kind !== 'unconfirmed') return save;
  return mirroredPaused === save.paused ? IDLE : save;
}

/** The echo never came. Say so, rather than claiming the command is still moving. */
export function timeOutAlarmsPause(save: AlarmsPauseSaveState): AlarmsPauseSaveState {
  return save.kind === 'sent' ? { kind: 'unconfirmed', paused: save.paused } : save;
}

const IDLE: AlarmsPauseSaveState = { kind: 'idle' };

/**
 * Copy for each non-idle state. `null` when there is nothing left to say —
 * which is what happens the moment the mirror agrees with what was sent, because
 * from then on the banner itself is the answer.
 */
export function alarmsPauseStatusText(state: AlarmsPauseSaveState): string | null {
  switch (state.kind) {
    case 'sending':
      return 'Sending to your boat…';
    case 'sent':
      return 'Sent to your boat — this page updates when your boat confirms it.';
    case 'unconfirmed':
      // Not an error and not a success. The relay accepted it (or staged it for
      // a boat that is not listening yet), so the instruction is not lost — but
      // nothing has stopped or started watching until the boat says so.
      return 'Your boat has not confirmed that yet. It takes effect when your boat picks it up, and this page updates then.';
    case 'offline':
      // Naming which half of the round trip is missing, rather than "failed":
      // the boat is what stops and starts watching, so an unreachable boat is
      // not a cloud error to retry blindly.
      return state.paused
        ? 'Your boat is offline. Your boat is what stops watching, so bring it online and try again.'
        : 'Your boat is offline. Your boat is what starts watching again, so bring it online and try again.';
    case 'error':
      return 'Couldn’t send that to your boat — try again.';
    case 'idle':
      return null;
  }
}

/**
 * Relay one instruction to the boat. Resolves to the state the caller should
 * show; it never throws, because every failure here has a sentence above.
 */
export async function sendAlarmsPause(
  boatId: string,
  paused: boolean
): Promise<AlarmsPauseSaveState> {
  try {
    await api(`/v1/boats/${boatId}/alarms-pause`, {
      method: 'POST',
      body: { paused },
      idempotencyKey: crypto.randomUUID(),
    });
    return { kind: 'sent', paused };
  } catch (err) {
    return isBoatOfflineRelayError(err) ? { kind: 'offline', paused } : { kind: 'error' };
  }
}
